import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from '../../i18n/i18n';
import { ChevronDownIcon } from '../icons/Icons';
import './TimeField.scss';

interface TimeFieldProps {
  /** "HH:MM", 24-hour; empty while unset. */
  value: string;
  onChange: (value: string) => void;
  labelledBy: string;
  invalid?: boolean;
}

const pad = (n: number) => String(n).padStart(2, '0');
const HOURS = Array.from({ length: 24 }, (_, i) => pad(i));
const MINUTES = Array.from({ length: 60 }, (_, i) => pad(i));

/**
 * A 24-hour time picker: the value as a button and, below it, an hours column and a minutes column.
 * Replaces `<input type="time">`, which follows the browser locale and can show AM/PM.
 */
const TimeField = ({ value, onChange, labelledBy, invalid = false }: TimeFieldProps) => {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const valueId = useId();
  const [hour = '', minute = ''] = /^\d{2}:\d{2}$/.test(value) ? value.split(':') : [];

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  // Bring the picked hour and minute into view when the columns open.
  useLayoutEffect(() => {
    if (!open || !root.current) return;
    root.current.querySelectorAll<HTMLElement>('[aria-selected="true"]').forEach((el) => {
      const col = el.parentElement;
      if (col) col.scrollTop = el.offsetTop - col.clientHeight / 2 + el.clientHeight / 2;
    });
    const selected = root.current.querySelector<HTMLElement>('[aria-selected="true"]');
    (selected ?? root.current.querySelector<HTMLElement>('[role="option"]'))?.focus({ preventScroll: true });
  }, [open]);

  const pick = (h: string, m: string, close: boolean) => {
    onChange(`${h}:${m}`);
    if (close) setOpen(false);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    // Keep the Escape from also closing the period dialog around this field.
    e.stopPropagation();
    setOpen(false);
    root.current?.querySelector<HTMLElement>('.TimeField__button')?.focus();
  };

  const onOptionKey = (e: React.KeyboardEvent<HTMLElement>) => {
    const step = e.key === 'ArrowDown' ? 1 : e.key === 'ArrowUp' ? -1 : 0;
    if (step) {
      e.preventDefault();
      const next = (
        step > 0 ? e.currentTarget.nextElementSibling : e.currentTarget.previousElementSibling
      ) as HTMLElement | null;
      next?.focus();
      next?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.currentTarget.click();
    }
  };

  const column = (kind: 'hour' | 'minute', items: string[], selected: string) => (
    <div role="listbox" aria-label={t(`period.${kind}`)} className="TimeField__column">
      {items.map((v) => (
        <div
          key={v}
          role="option"
          tabIndex={-1}
          aria-selected={v === selected}
          className={`TimeField__option${v === selected ? ' TimeField__option--selected' : ''}`}
          onClick={() => (kind === 'hour' ? pick(v, minute || '00', false) : pick(hour || '00', v, true))}
          onKeyDown={onOptionKey}
        >
          {v}
        </div>
      ))}
    </div>
  );

  return (
    <div ref={root} className="TimeField" onKeyDown={onKey}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${labelledBy} ${valueId}`}
        className={`TimeField__button${invalid ? ' TimeField__button--invalid' : ''}`}
        onClick={() => setOpen(!open)}
      >
        <span id={valueId}>{hour && minute ? `${hour}:${minute}` : '--:--'}</span>
        <ChevronDownIcon />
      </button>
      {open && (
        <div className="TimeField__popover">
          <div aria-hidden="true" className="TimeField__heads">
            <span>{t('period.hour')}</span>
            <span>{t('period.minute')}</span>
          </div>
          <div className="TimeField__columns">
            {column('hour', HOURS, hour)}
            {column('minute', MINUTES, minute)}
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeField;
